// src/components/schedule/ScheduleModal.jsx
import React, { useState } from 'react';
import SyncScheduleService from '../../services/SyncScheduleService';
import { toast } from 'react-toastify';

/* ── Constants ───────────────────────────────────────────── */
const DATA_TYPES = [
  { value: 'courses',   label: 'Courses',           icon: 'bi-book' },
  { value: 'chapters',  label: 'Chapters',          icon: 'bi-list-nested' },
  { value: 'content',   label: 'Chapter Content',   icon: 'bi-file-earmark-text' },
  // Refreshes only the `Live Class` bucket chapters, so schedule healing has the
  // day's video conferences to match against — see LiveClassContentService.
  { value: 'live-class-content', label: 'Live Class Content', icon: 'bi-camera-video' },
  // The JEE counterpart: `Video lectures`, `Unit test` and the course-wide test
  // roots, scoped by the JEE timetable in Schedule — see JeeContentService.
  { value: 'jee-live-class-content', label: 'JEE Live Class Content', icon: 'bi-camera-video' },
  { value: 'mentors',   label: 'Mentors',           icon: 'bi-person' },
  { value: 'exams',     label: 'Exams',             icon: 'bi-card-list' },
  { value: 'attempts',  label: 'Attempts',          icon: 'bi-clipboard-check' },
  { value: 'batches',   label: 'Batches',           icon: 'bi-people' },
  { value: 'users',     label: 'Users',             icon: 'bi-people-fill' },
  { value: 'all',       label: 'All Data',          icon: 'bi-arrow-repeat' },
];

/**
 * Frequencies the scheduler understands — these must stay in sync with the
 * `switch (frequency)` in `buildCronExpression` (util/scheduler.js).
 */
const FREQUENCIES = [
  { value: 'hourly',  label: 'Hourly',  hint: 'Runs every hour at the selected minute.' },
  { value: 'daily',   label: 'Daily',   hint: 'Runs once a day at the selected time.' },
  { value: 'weekly',  label: 'Weekly',  hint: 'Runs on the selected days at the selected time.' },
  { value: 'monthly', label: 'Monthly', hint: 'Runs once a month at the selected time.' },
  { value: 'once',    label: 'Once',    hint: 'Runs a single time at the selected date and time.' },
];

/** Order matters — this is the Sun-first order the cron day-of-week field uses. */
const WEEK_DAYS = [
  { key: 'sunday',    short: 'Sun' },
  { key: 'monday',    short: 'Mon' },
  { key: 'tuesday',   short: 'Tue' },
  { key: 'wednesday', short: 'Wed' },
  { key: 'thursday',  short: 'Thu' },
  { key: 'friday',    short: 'Fri' },
  { key: 'saturday',  short: 'Sat' },
];

const EMPTY_WEEK_DAYS = WEEK_DAYS.reduce((acc, d) => ({ ...acc, [d.key]: false }), {});

const DEFAULT_FORM = {
  dataType: 'courses',
  frequency: 'daily',
  startTime: '00:00',
  startDate: '',
  weekDays: { ...EMPTY_WEEK_DAYS },
  monthlyOption: 'dayOfMonth',
  dayOfMonth: 1,
  enabled: true,
};

/** Preview the resulting schedule in words, mirroring buildCronExpression. */
const describeSchedule = (form) => {
  const time = form.startTime || '00:00';
  const minute = time.split(':')[1] || '00';

  switch (form.frequency) {
    case 'hourly':
      return `Every hour at :${minute}`;
    case 'daily':
      return `Every day at ${time}`;
    case 'weekly': {
      const days = WEEK_DAYS.filter((d) => form.weekDays[d.key]).map((d) => d.short);
      if (!days.length) return 'Select at least one day';
      if (days.length === 7) return `Every day at ${time}`;
      return `Every ${days.join(', ')} at ${time}`;
    }
    case 'monthly':
      // "Last day" runs on the 28th — see the node-cron note in util/cronExpression.js.
      return form.monthlyOption === 'lastDay'
        ? `On day 28 of each month at ${time}`
        : `On day ${form.dayOfMonth || 1} of each month at ${time}`;
    case 'once':
      return form.startDate
        ? `Once on ${form.startDate} at ${time}`
        : 'Select a date';
    default:
      return '';
  }
};

/* ── ScheduleModal ───────────────────────────────────────── */
const ScheduleModal = ({ schedule, onClose, onSaved }) => {
  const isEdit = Boolean(schedule);

  const [form, setForm] = useState(() => {
    if (!isEdit) return { ...DEFAULT_FORM };
    return {
      dataType:  schedule.dataType  || 'courses',
      frequency: schedule.frequency || 'daily',
      startTime: schedule.startTime || '00:00',
      startDate: schedule.startDate ? String(schedule.startDate).slice(0, 10) : '',
      // Stored as JSON; merge over the empty map so a partial object still
      // renders every toggle.
      weekDays: { ...EMPTY_WEEK_DAYS, ...(schedule.weekDays || {}) },
      monthlyOption: schedule.monthlyOption === 'lastDay' ? 'lastDay' : 'dayOfMonth',
      dayOfMonth: schedule.dayOfMonth || 1,
      enabled: schedule.enabled !== undefined ? schedule.enabled : true,
    };
  });

  const [isSaving, setIsSaving] = useState(false);

  /* ── Handlers ──────────────────────────────────────────── */
  const handleField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const toggleDay = (key) => {
    setForm((f) => ({ ...f, weekDays: { ...f.weekDays, [key]: !f.weekDays[key] } }));
  };

  const setAllDays = (value) => {
    setForm((f) => ({
      ...f,
      weekDays: WEEK_DAYS.reduce((acc, d) => ({ ...acc, [d.key]: value }), {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.dataType) {
      toast.error('Please select a data type');
      return;
    }
    if (form.frequency === 'weekly' && !WEEK_DAYS.some((d) => form.weekDays[d.key])) {
      toast.error('Select at least one day of the week');
      return;
    }
    if (form.frequency === 'once' && !form.startDate) {
      toast.error('Select a date for a one-off schedule');
      return;
    }
    if (form.frequency === 'monthly' && form.monthlyOption === 'dayOfMonth') {
      const day = Number(form.dayOfMonth);
      if (!Number.isInteger(day) || day < 1 || day > 31) {
        toast.error('Day of month must be between 1 and 31');
        return;
      }
    }

    // Send only the fields relevant to the chosen frequency, so switching away
    // from weekly/monthly does not leave stale day selections on the row.
    const payload = {
      dataType: form.dataType,
      frequency: form.frequency,
      startTime: form.startTime || '00:00',
      enabled: form.enabled,
      weekDays: form.frequency === 'weekly' ? form.weekDays : null,
      monthlyOption:
        form.frequency === 'monthly' && form.monthlyOption === 'lastDay' ? 'lastDay' : null,
      dayOfMonth:
        form.frequency === 'monthly' && form.monthlyOption === 'dayOfMonth'
          ? Number(form.dayOfMonth)
          : null,
      startDate: form.frequency === 'once' && form.startDate ? form.startDate : null,
    };

    setIsSaving(true);
    try {
      let result;
      if (isEdit) {
        result = await SyncScheduleService.updateSchedule(schedule.id, payload);
        toast.success('Schedule updated successfully');
      } else {
        result = await SyncScheduleService.saveSchedule(payload);
        toast.success('Schedule created successfully');
      }
      if (onSaved) onSaved(result?.data || result);
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (isEdit ? 'Failed to update schedule' : 'Failed to create schedule');
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const activeFrequency = FREQUENCIES.find((f) => f.value === form.frequency);
  const selectedDayCount = WEEK_DAYS.filter((d) => form.weekDays[d.key]).length;

  /* ── Render ────────────────────────────────────────────── */
  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="schedule-modal-header">
          <h2 className="schedule-modal-title">
            <i
              className={`bi ${isEdit ? 'bi-pencil-square' : 'bi-plus-circle'}`}
              style={{ color: 'var(--accent-blue)' }}
              aria-hidden="true"
            />
            {isEdit ? 'Edit Schedule' : 'New Schedule'}
          </h2>
          <button
            className="schedule-modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="schedule-modal-body">
            {/* Data type */}
            <div className="schedule-form-group">
              <label className="schedule-form-label" htmlFor="sched-data-type">
                Data Type
              </label>
              <select
                id="sched-data-type"
                className="schedule-form-select"
                value={form.dataType}
                onChange={handleField('dataType')}
                disabled={isSaving}
                required
              >
                {DATA_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Frequency */}
            <div className="schedule-form-group">
              <label className="schedule-form-label" htmlFor="sched-frequency">
                Frequency
              </label>
              <select
                id="sched-frequency"
                className="schedule-form-select"
                value={form.frequency}
                onChange={handleField('frequency')}
                disabled={isSaving}
              >
                {FREQUENCIES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              {activeFrequency?.hint && (
                <span className="schedule-form-hint">{activeFrequency.hint}</span>
              )}
            </div>

            {/* Weekly day picker */}
            {form.frequency === 'weekly' && (
              <div className="schedule-form-group">
                <label className="schedule-form-label">
                  Repeat on
                  <span className="schedule-day-count">
                    {selectedDayCount
                      ? `${selectedDayCount} day${selectedDayCount === 1 ? '' : 's'}`
                      : 'none selected'}
                  </span>
                </label>
                <div className="schedule-day-picker" role="group" aria-label="Days of the week">
                  {WEEK_DAYS.map(({ key, short }) => (
                    <button
                      key={key}
                      type="button"
                      className={`schedule-day-btn${form.weekDays[key] ? ' active' : ''}`}
                      onClick={() => toggleDay(key)}
                      disabled={isSaving}
                      aria-pressed={form.weekDays[key]}
                    >
                      {short}
                    </button>
                  ))}
                </div>
                <div className="schedule-day-shortcuts">
                  <button type="button" onClick={() => setAllDays(true)} disabled={isSaving}>
                    Every day
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({
                      ...f,
                      weekDays: {
                        ...EMPTY_WEEK_DAYS,
                        monday: true, tuesday: true, wednesday: true,
                        thursday: true, friday: true,
                      },
                    }))}
                    disabled={isSaving}
                  >
                    Weekdays
                  </button>
                  <button type="button" onClick={() => setAllDays(false)} disabled={isSaving}>
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Monthly day selection */}
            {form.frequency === 'monthly' && (
              <div className="schedule-form-group">
                <label className="schedule-form-label" htmlFor="sched-monthly-option">
                  Day of month
                </label>
                <div className="schedule-monthly-row">
                  <select
                    id="sched-monthly-option"
                    className="schedule-form-select"
                    value={form.monthlyOption}
                    onChange={handleField('monthlyOption')}
                    disabled={isSaving}
                  >
                    <option value="dayOfMonth">On a specific day</option>
                    <option value="lastDay">On the last day (28th)</option>
                  </select>
                  {form.monthlyOption === 'dayOfMonth' && (
                    <input
                      type="number"
                      className="schedule-form-input schedule-day-of-month"
                      min="1"
                      max="31"
                      value={form.dayOfMonth}
                      onChange={handleField('dayOfMonth')}
                      disabled={isSaving}
                      aria-label="Day of month"
                    />
                  )}
                </div>
                {form.monthlyOption === 'dayOfMonth' && Number(form.dayOfMonth) > 28 && (
                  <span className="schedule-form-hint schedule-form-warn">
                    Months shorter than day {form.dayOfMonth} will skip this run.
                  </span>
                )}
              </div>
            )}

            {/* One-off date */}
            {form.frequency === 'once' && (
              <div className="schedule-form-group">
                <label className="schedule-form-label" htmlFor="sched-start-date">
                  Date
                </label>
                <input
                  id="sched-start-date"
                  type="date"
                  className="schedule-form-input"
                  value={form.startDate}
                  onChange={handleField('startDate')}
                  disabled={isSaving}
                  required
                />
              </div>
            )}

            {/* Time */}
            <div className="schedule-form-group">
              <label className="schedule-form-label" htmlFor="sched-start-time">
                {form.frequency === 'hourly' ? 'Minute past the hour' : 'Time'}
              </label>
              <input
                id="sched-start-time"
                type="time"
                className="schedule-form-input"
                value={form.startTime}
                onChange={handleField('startTime')}
                disabled={isSaving}
                required
              />
              <span className="schedule-form-hint">
                {form.frequency === 'hourly'
                  ? 'Only the minutes are used — the sync runs every hour at that minute.'
                  : 'Server timezone: Asia/Kolkata (IST).'}
              </span>
            </div>

            {/* Plain-language preview */}
            <div className="schedule-preview">
              <i className="bi bi-clock-history" aria-hidden="true" />
              <span>{describeSchedule(form)}</span>
            </div>

            {/* Active toggle */}
            <div className="schedule-active-row">
              <div>
                <div className="schedule-active-label">Active</div>
                <div className="schedule-active-desc">
                  {form.enabled ? 'Schedule will run automatically' : 'Schedule is paused'}
                </div>
              </div>
              <label className="schedule-toggle" aria-label="Toggle active">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={handleField('enabled')}
                  disabled={isSaving}
                />
                <span className="schedule-toggle-track" />
                <span className="schedule-toggle-thumb" />
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="schedule-modal-footer">
            <button
              type="button"
              className="qa-btn qa-btn--ghost"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="submit" className="qa-btn qa-btn--blue" disabled={isSaving}>
              {isSaving ? (
                <>
                  <span className="schedule-spinner" aria-hidden="true" />
                  {isEdit ? 'Saving…' : 'Creating…'}
                </>
              ) : (
                <>
                  <i className={`bi ${isEdit ? 'bi-check-lg' : 'bi-plus-lg'}`} aria-hidden="true" />
                  {isEdit ? 'Save Changes' : 'Create Schedule'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleModal;
